import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import debounce from "lodash/debounce";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type { VisibilityScope } from "@/components/agentic-v2/VisibilityFilter";
import { useCompanyId } from "@/context/CompanyContext";
import { useWindowLive } from "@/components/ui/os-layout/WindowActiveContext";
import type {
  MissionControlEventSubscription,
  V2TaskCreatedPayload,
  V2TaskNeedsAttentionPayload,
  V2TaskStatusChangedPayload,
} from "@/context/MissionControlWebSocketContext";
import { clientsKeys, type ClientTasksKeyInput } from "./keys";
import {
  applyTaskCreatedToMissionDetailCache,
  applyTaskStatusChangedToMissionDetailCache,
  invalidateClientTasks,
  patchTaskInClientList,
  patchTaskStatusInClientList,
  taskBelongsToFaClientScope,
} from "./patches";
import { v2AgentRefToTaskAgentRef } from "./wsTaskMerge";

export interface UseClientTasksOptions {
  faClientId: string;
  visibility?: VisibilityScope;
  enabled?: boolean;
}

/** WebSocket handlers that patch or invalidate the cached client task list. */
export function createClientTasksWsSubscription(
  queryClient: QueryClient,
  faClientId: string,
  keyInput: ClientTasksKeyInput,
  debouncedInvalidate: () => void,
): MissionControlEventSubscription {
  return {
    onTaskCreated: (payload: V2TaskCreatedPayload) => {
      if (!taskBelongsToFaClientScope(queryClient, faClientId, payload)) return;
      applyTaskCreatedToMissionDetailCache(queryClient, payload);
      const fallbackAgent = v2AgentRefToTaskAgentRef(payload.agent);
      if (payload.task) {
        patchTaskInClientList(queryClient, keyInput, payload.task, fallbackAgent);
        return;
      }
      debouncedInvalidate();
    },
    onTaskStatusChanged: (payload: V2TaskStatusChangedPayload) => {
      if (!taskBelongsToFaClientScope(queryClient, faClientId, payload)) return;
      applyTaskStatusChangedToMissionDetailCache(queryClient, payload);
      const fallbackAgent = v2AgentRefToTaskAgentRef(payload.agent);
      if (payload.task) {
        patchTaskInClientList(queryClient, keyInput, payload.task, fallbackAgent);
        return;
      }
      patchTaskStatusInClientList(queryClient, keyInput, {
        task_id: payload.task_id,
        new_status: payload.new_status,
      });
      debouncedInvalidate();
    },
    onTaskNeedsAttention: (payload: V2TaskNeedsAttentionPayload) => {
      if (!taskBelongsToFaClientScope(queryClient, faClientId, payload)) return;
      debouncedInvalidate();
    },
  };
}

/** All tasks acting on a specific fa_client across every mission. */
export function useClientTasks({
  faClientId,
  visibility = "team",
  enabled: enabledOption = true,
}: UseClientTasksOptions) {
  const isWindowLive = useWindowLive();
  const companyId = useCompanyId();
  const queryEnabled = Boolean(faClientId) && enabledOption && isWindowLive;
  const keyInput = useMemo(
    () => ({
      faClientId,
      visibility,
      companyId: companyId ?? undefined,
    }),
    [faClientId, visibility, companyId],
  );

  const query = useQuery({
    queryKey: clientsKeys.tasks(keyInput),
    enabled: queryEnabled,
    refetchOnWindowFocus: queryEnabled,
    queryFn: async () => {
      const page = await AgenticV2API.listTasks("all", { faClientId, visibility });
      return page.items;
    },
  });

  return {
    tasks: query.data ?? [],
    loading: query.isLoading,
    error: query.error,
    refresh: () => void query.refetch(),
    keyInput,
  };
}

/** QueryClient accessor for client tasks cache patches. */
export function useClientTasksQueryClient() {
  return useQueryClient();
}

/** Debounced bucket invalidation for client tasks when WS payloads are incomplete. */
export function createDebouncedClientTasksInvalidate(
  queryClient: QueryClient,
  keyInput: ClientTasksKeyInput,
  waitMs = 500,
): { invalidate: () => void; cancel: () => void } {
  const debounced = debounce(() => {
    invalidateClientTasks(queryClient, keyInput);
  }, waitMs);
  return {
    invalidate: () => debounced(),
    cancel: () => debounced.cancel(),
  };
}
